import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import useTableSort from "../hooks/useTableSort";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import "./ReportsSectionCompact.css";
import {
  getServiceAnalyticsOverview,
  getServiceTotals,
  getServiceMixByEmployee,
  getTopServices,
  getClientsByService,
  getServiceRetention,
  getServiceCombos,
  getServiceDiscountIntegrity,
  getServiceRefunds,
  getServiceCapacity,
} from "../api/reports";
import { listStaff } from "../api/staffs";
import { listCategories, listServices } from "../api/services";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
  TAB_BASE,
  TAB_ACTIVE,
  TAB_INACTIVE,
} from "../ui/modalStyles";
import { getSalonTimezone } from "../utils/salonTimezone";
import { SERVICE_ANALYTICS_TOOLTIPS } from "../constants/reportTooltips";
import { REPORT_HELP } from "../constants/reportHelpContent";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

const isDateOnlyISO = (value) => DATE_ONLY_RE.test((value || "").trim());

const parseBoolParam = (value, fallback) => {
  const normalized = (value || "").trim().toLowerCase();
  if (!normalized) return fallback;
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return fallback;
};

const parseIdParam = (value) => {
  const normalized = (value || "").trim();
  return /^\d+$/.test(normalized) ? normalized : "";
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);
  if (Number.isNaN(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

const formatNumber = (value, digits = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return num.toFixed(digits);
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${num.toFixed(1)}%`;
};

const formatMinutes = (value) => {
  if (value === null || value === undefined) return "--";
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return "--";
  return minutes.toFixed(1);
};

const formatHours = (minutesValue) => {
  const minutes = Number(minutesValue);
  if (!Number.isFinite(minutes) || minutes <= 0) return "--";
  return (minutes / 60).toFixed(1);
};

const CAP_OPTIONS = [
  { value: "2", label: "2" },
  { value: "4", label: "4" },
  { value: "6", label: "6" },
  { value: "8", label: "8" },
  { value: "12", label: "12" },
  { value: "none", label: "No cap" },
];

const GROUP_BY_OPTIONS = [
  { value: "", label: "No grouping" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const TOP_N_OPTIONS = [
  { value: 10, label: "Top 10" },
  { value: 20, label: "Top 20" },
  { value: 50, label: "Top 50" },
];

const TOP_SORT_OPTIONS = [
  { value: "net", label: "Net Sales" },
  { value: "qty", label: "Quantity" },
];

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "mixByEmployee", label: "Mix by Employee" },
  { key: "servicePerformance", label: "Service Performance" },
  { key: "topServices", label: "Top Services" },
  { key: "clientsByService", label: "Clients by Service" },
  { key: "retention", label: "Retention" },
  { key: "combos", label: "Add-ons / Combos" },
  { key: "discounts", label: "Discounts" },
  { key: "refunds", label: "Refunds" },
  { key: "capacity", label: "Capacity" },
];

const TAB_ALIASES = {
  overview: "overview",
  mix: "mixByEmployee",
  mixbyemployee: "mixByEmployee",
  performance: "servicePerformance",
  serviceperformance: "servicePerformance",
  top: "topServices",
  topservices: "topServices",
  clients: "clientsByService",
  clientsbyservice: "clientsByService",
  retention: "retention",
  combos: "combos",
  discounts: "discounts",
  refunds: "refunds",
  capacity: "capacity",
};

const normalizeTabParam = (value) => {
  const normalized = (value || "").trim().toLowerCase();
  const mapped = TAB_ALIASES[normalized] || normalized;
  return TABS.some((tab) => tab.key === mapped) ? mapped : "overview";
};

const TOOLTIP_MAX_WIDTH = 240;
const SORT_ICON = { asc: "^", desc: "v" };
const TAB_TOOLTIP_KEYS = {
  overview: "tab_overview",
  mixByEmployee: "tab_mix_by_employee",
  servicePerformance: "tab_service_performance",
  topServices: "tab_top_services",
  clientsByService: "tab_clients_by_service",
  retention: "tab_retention",
  combos: "tab_addons_combos",
  discounts: "tab_discounts",
  refunds: "tab_refunds",
  capacity: "tab_capacity",
};

const SERVICE_ANALYTICS_COLUMNS = {
  overviewTrend: {
    columns: {
      period: { type: "text", getValue: (row) => row?.period },
      net_sales: { type: "number", getValue: (row) => row?.net_sales },
      qty: { type: "number", getValue: (row) => row?.qty },
    },
    fallbackKey: "period",
  },
  servicePerformance: {
    columns: {
      service: { type: "text", getValue: (row) => row?.service_name },
      qty: { type: "number", getValue: (row) => row?.qty },
      gross: { type: "number", getValue: (row) => row?.gross_sales },
      discount: { type: "number", getValue: (row) => row?.discount_amount },
      net: { type: "number", getValue: (row) => row?.net_sales },
      percent_net: { type: "number", getValue: (row) => row?.percent_of_total_net },
      percent_qty: { type: "number", getValue: (row) => row?.percent_of_total_qty },
      avg_duration: { type: "number", getValue: (row) => row?.avg_duration_minutes },
      total_duration: { type: "number", getValue: (row) => row?.total_duration_minutes },
    },
    fallbackKey: "service",
  },
  mixByEmployee: {
    columns: {
      employee: { type: "text", getValue: (row) => row?.staff_name },
      service: { type: "text", getValue: (row) => row?.service_name },
      qty: { type: "number", getValue: (row) => row?.qty },
      net: { type: "number", getValue: (row) => row?.net_sales },
      percent_employee: {
        type: "number",
        getValue: (row) => row?.percent_of_employee_net,
      },
      avg_duration: { type: "number", getValue: (row) => row?.avg_duration_minutes },
      total_duration: { type: "number", getValue: (row) => row?.total_duration_minutes },
    },
    fallbackKey: "service",
  },
  topServices: {
    columns: {
      service: { type: "text", getValue: (row) => row?.service_name },
      qty: { type: "number", getValue: (row) => row?.qty },
      net_sales: { type: "number", getValue: (row) => row?.net_sales },
      percent_total: { type: "number", getValue: (row) => row?.percent_of_total },
    },
    fallbackKey: "service",
  },
  clientsByService: {
    columns: {
      client: { type: "text", getValue: (row) => row?.client_name },
      times: { type: "number", getValue: (row) => row?.times_purchased_in_range },
      net_spend: {
        type: "number",
        getValue: (row) => row?.total_spend_on_service_net,
      },
      last_purchase: {
        type: "date",
        getValue: (row) => row?.last_time_purchased_this_service,
      },
      last_visit: {
        type: "date",
        getValue: (row) => row?.last_visit_date_overall,
      },
    },
    fallbackKey: "client",
  },
  retention: {
    columns: {
      service: { type: "text", getValue: (row) => row?.service_name },
      unique_clients: {
        type: "number",
        getValue: (row) => row?.unique_clients_in_range,
      },
      new_clients: { type: "number", getValue: (row) => row?.new_clients_for_service },
      repeat_clients: { type: "number", getValue: (row) => row?.repeat_clients },
      repeat_30: { type: "number", getValue: (row) => row?.repeat_rate_30 },
      repeat_60: { type: "number", getValue: (row) => row?.repeat_rate_60 },
      repeat_90: { type: "number", getValue: (row) => row?.repeat_rate_90 },
    },
    fallbackKey: "service",
  },
  combos: {
    columns: {
      service_a: { type: "text", getValue: (row) => row?.service_a_name },
      service_b: { type: "text", getValue: (row) => row?.service_b_name },
      bundle_count: { type: "number", getValue: (row) => row?.bundle_count },
      bundle_net: { type: "number", getValue: (row) => row?.bundle_net_sales },
      attach_rate: { type: "number", getValue: (row) => row?.attach_rate },
    },
    fallbackKey: "service_a",
  },
  discounts: {
    columns: {
      service: { type: "text", getValue: (row) => row?.service_name },
      qty: { type: "number", getValue: (row) => row?.qty },
      gross: { type: "number", getValue: (row) => row?.gross_sales },
      discount: { type: "number", getValue: (row) => row?.discount_amount },
      discount_percent: { type: "number", getValue: (row) => row?.discount_percent },
      net: { type: "number", getValue: (row) => row?.net_sales },
      avg_net: { type: "number", getValue: (row) => row?.avg_net_price },
      base_price: { type: "number", getValue: (row) => row?.base_price },
    },
    fallbackKey: "service",
  },
  refunds: {
    columns: {
      service: { type: "text", getValue: (row) => row?.service_name },
      refund_qty: { type: "number", getValue: (row) => row?.refund_qty },
      refund_amount: { type: "number", getValue: (row) => row?.refund_amount },
      refund_rate: { type: "number", getValue: (row) => row?.refund_rate_percent },
    },
    fallbackKey: "service",
  },
  capacity: {
    columns: {
      service: { type: "text", getValue: (row) => row?.service_name },
      qty: { type: "number", getValue: (row) => row?.qty },
      net_sales: { type: "number", getValue: (row) => row?.net_sales },
      total_hours: { type: "number", getValue: (row) => row?.total_hours_sold },
      net_per_hour: { type: "number", getValue: (row) => row?.net_per_hour },
    },
    fallbackKey: "service",
  },
};

export default function ServiceAnalyticsReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentSalon, user, loading: authLoading } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.SERVICE_ANALYTICS;
  const resolvedSalonId = useMemo(() => {
    const raw = currentSalon?.id ?? currentSalon?.salon_id ?? user?.salon_id ?? null;
    if (raw === null || raw === undefined || raw === "") return null;
    const asNum = Number(raw);
    return Number.isFinite(asNum) ? asNum : null;
  }, [currentSalon, user?.salon_id]);
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );

  const today = formatDateISO(new Date(), salonTimezone);
  const defaultStart = formatDateISO(addDays(new Date(), -6), salonTimezone);
  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/manager/reports", { replace: true });
  };

  const initialStartDate = isDateOnlyISO(searchParams.get("start"))
    ? searchParams.get("start")
    : defaultStart;
  const initialEndDate = isDateOnlyISO(searchParams.get("end"))
    ? searchParams.get("end")
    : today;
  const initialEmployeeId = parseIdParam(searchParams.get("employee_id"));
  const initialCategoryId = parseIdParam(
    searchParams.get("service_category_id") || searchParams.get("category_id")
  );
  const initialIncludeRefunds = parseBoolParam(
    searchParams.get("include_refunds"),
    false
  );
  const initialIncludeDiscounts = parseBoolParam(
    searchParams.get("include_discounts"),
    true
  );
  const initialActiveTab = normalizeTabParam(searchParams.get("tab"));
  const initialClientsServiceId = parseIdParam(searchParams.get("service_id"));

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [employeeId, setEmployeeId] = useState(initialEmployeeId);
  const [categoryId, setCategoryId] = useState(initialCategoryId);
  const [includeRefunds, setIncludeRefunds] = useState(initialIncludeRefunds);
  const [includeDiscounts, setIncludeDiscounts] = useState(initialIncludeDiscounts);
  const [groupBy, setGroupBy] = useState("");
  const [durationMode, setDurationMode] = useState("ALLOCATED");
  const [outlierCap, setOutlierCap] = useState("6");
  const [topN, setTopN] = useState(10);
  const [topSort, setTopSort] = useState("net");
  const [activeTab, setActiveTab] = useState(initialActiveTab);

  const [overview, setOverview] = useState(null);
  const [serviceTotals, setServiceTotals] = useState([]);
  const [serviceMix, setServiceMix] = useState([]);
  const [topServices, setTopServices] = useState([]);
  const [clientsByService, setClientsByService] = useState([]);
  const [retentionRows, setRetentionRows] = useState([]);
  const [combosRows, setCombosRows] = useState([]);
  const [discountRows, setDiscountRows] = useState([]);
  const [refundRows, setRefundRows] = useState([]);
  const [capacityRows, setCapacityRows] = useState([]);

  const [staffOptions, setStaffOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [clientsServiceId, setClientsServiceId] = useState(initialClientsServiceId);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sharedParams = useMemo(() => {
    const capValue = outlierCap === "none" ? null : Number(outlierCap);
    const params = {
      start: startDate,
      end: endDate,
      include_refunds: includeRefunds,
      include_discounts: includeDiscounts,
      duration_mode: durationMode,
    };
    if (employeeId) {
      params.employee_id = Number(employeeId);
    }
    if (categoryId) {
      params.service_category_id = Number(categoryId);
    }
    if (groupBy) {
      params.group_by = groupBy;
    }
    if (capValue !== null && Number.isFinite(capValue)) {
      params.duration_outlier_cap_hours = capValue;
    }
    return params;
  }, [
    startDate,
    endDate,
    includeRefunds,
    includeDiscounts,
    durationMode,
    employeeId,
    categoryId,
    groupBy,
    outlierCap,
  ]);

  const serviceCategoryOptions = useMemo(
    () =>
      categories.filter((category) =>
        ["service", "both"].includes((category?.kind || "both").toLowerCase())
      ),
    [categories]
  );

  const loadStaff = useCallback(async () => {
    if (!resolvedSalonId) return;
    try {
      const staff = await listStaff({ salon_id: resolvedSalonId });
      const rows = Array.isArray(staff)
        ? staff
        : Array.isArray(staff?.rows)
        ? staff.rows
        : [];
      setStaffOptions(rows);
    } catch (err) {
      setStaffOptions([]);
    }
  }, [resolvedSalonId]);

  const loadServices = useCallback(async () => {
    if (!resolvedSalonId) return;
    try {
      const services = await listServices({ salon_id: resolvedSalonId });
      setServiceOptions(Array.isArray(services) ? services : []);
    } catch (err) {
      setServiceOptions([]);
    }
  }, [resolvedSalonId]);

  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const data = await listCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && resolvedSalonId) {
      loadStaff();
      loadServices();
    }
  }, [authLoading, resolvedSalonId, loadStaff, loadServices]);

  useEffect(() => {
    if (authLoading) return;
    loadCategories();
  }, [authLoading, loadCategories]);

  const fetchAll = useCallback(async () => {
    if (authLoading) return;
    if (!resolvedSalonId) {
      setError(
        "Unable to determine your current salon. Please refresh or re-select your salon."
      );
      return;
    }
    if (!startDate || !endDate) {
      setError("Select a valid start and end date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [
        overviewRes,
        totalsRes,
        mixRes,
        topRes,
        retentionRes,
        combosRes,
        discountRes,
        refundRes,
        capacityRes,
      ] = await Promise.all([
        getServiceAnalyticsOverview(sharedParams),
        getServiceTotals(sharedParams),
        getServiceMixByEmployee(sharedParams),
        getTopServices({ ...sharedParams, top_n: topN, sort_by: topSort }),
        getServiceRetention(sharedParams),
        getServiceCombos(sharedParams),
        getServiceDiscountIntegrity(sharedParams),
        getServiceRefunds(sharedParams),
        getServiceCapacity(sharedParams),
      ]);
      setOverview(overviewRes || null);
      setServiceTotals(Array.isArray(totalsRes?.rows) ? totalsRes.rows : []);
      setServiceMix(Array.isArray(mixRes?.rows) ? mixRes.rows : []);
      setTopServices(Array.isArray(topRes?.rows) ? topRes.rows : []);
      setRetentionRows(Array.isArray(retentionRes?.rows) ? retentionRes.rows : []);
      setCombosRows(Array.isArray(combosRes?.rows) ? combosRes.rows : []);
      setDiscountRows(Array.isArray(discountRes?.rows) ? discountRes.rows : []);
      setRefundRows(Array.isArray(refundRes?.rows) ? refundRes.rows : []);
      setCapacityRows(Array.isArray(capacityRes?.rows) ? capacityRes.rows : []);
    } catch (err) {
      setOverview(null);
      setServiceTotals([]);
      setServiceMix([]);
      setTopServices([]);
      setRetentionRows([]);
      setCombosRows([]);
      setDiscountRows([]);
      setRefundRows([]);
      setCapacityRows([]);
      const detail = err?.response?.data?.detail;
      setError(detail || "Unable to load service analytics.");
    } finally {
      setLoading(false);
    }
  }, [
    authLoading,
    resolvedSalonId,
    startDate,
    endDate,
    sharedParams,
    topN,
    topSort,
  ]);

  const fetchClients = useCallback(async () => {
    if (!clientsServiceId) {
      setClientsByService([]);
      return;
    }
    try {
      const res = await getClientsByService({
        ...sharedParams,
        service_id: Number(clientsServiceId),
      });
      setClientsByService(Array.isArray(res?.rows) ? res.rows : []);
    } catch (err) {
      setClientsByService([]);
      setError("Unable to load clients by service.");
    }
  }, [clientsServiceId, sharedParams]);

  useEffect(() => {
    if (!authLoading && resolvedSalonId) {
      fetchAll();
    }
  }, [authLoading, resolvedSalonId, fetchAll]);

  useEffect(() => {
    if (activeTab === "clientsByService") {
      fetchClients();
    }
  }, [activeTab, fetchClients]);

  const resetFilters = () => {
    setStartDate(defaultStart);
    setEndDate(today);
    setEmployeeId("");
    setCategoryId("");
    setIncludeRefunds(false);
    setIncludeDiscounts(true);
    setGroupBy("");
    setDurationMode("ALLOCATED");
    setOutlierCap("6");
    setTopN(10);
    setTopSort("net");
  };

  const togglePillClass = (active) =>
    `${TAB_BASE} ${active ? TAB_ACTIVE : TAB_INACTIVE}`;

  const tiles = overview?.tiles || {};
  const series = Array.isArray(overview?.series) ? overview.series : [];

  const overviewSort = useTableSort({
    rows: series,
    columns: SERVICE_ANALYTICS_COLUMNS.overviewTrend.columns,
    fallbackKey: SERVICE_ANALYTICS_COLUMNS.overviewTrend.fallbackKey,
  });
  const serviceTotalsSort = useTableSort({
    rows: serviceTotals,
    columns: SERVICE_ANALYTICS_COLUMNS.servicePerformance.columns,
    fallbackKey: SERVICE_ANALYTICS_COLUMNS.servicePerformance.fallbackKey,
  });
  const mixByEmployeeSort = useTableSort({
    rows: serviceMix,
    columns: SERVICE_ANALYTICS_COLUMNS.mixByEmployee.columns,
    fallbackKey: SERVICE_ANALYTICS_COLUMNS.mixByEmployee.fallbackKey,
  });
  const topServicesSort = useTableSort({
    rows: topServices,
    columns: SERVICE_ANALYTICS_COLUMNS.topServices.columns,
    fallbackKey: SERVICE_ANALYTICS_COLUMNS.topServices.fallbackKey,
  });
  const clientsByServiceSort = useTableSort({
    rows: clientsByService,
    columns: SERVICE_ANALYTICS_COLUMNS.clientsByService.columns,
    fallbackKey: SERVICE_ANALYTICS_COLUMNS.clientsByService.fallbackKey,
  });
  const retentionSort = useTableSort({
    rows: retentionRows,
    columns: SERVICE_ANALYTICS_COLUMNS.retention.columns,
    fallbackKey: SERVICE_ANALYTICS_COLUMNS.retention.fallbackKey,
  });
  const combosSort = useTableSort({
    rows: combosRows,
    columns: SERVICE_ANALYTICS_COLUMNS.combos.columns,
    fallbackKey: SERVICE_ANALYTICS_COLUMNS.combos.fallbackKey,
  });
  const discountsSort = useTableSort({
    rows: discountRows,
    columns: SERVICE_ANALYTICS_COLUMNS.discounts.columns,
    fallbackKey: SERVICE_ANALYTICS_COLUMNS.discounts.fallbackKey,
  });
  const refundsSort = useTableSort({
    rows: refundRows,
    columns: SERVICE_ANALYTICS_COLUMNS.refunds.columns,
    fallbackKey: SERVICE_ANALYTICS_COLUMNS.refunds.fallbackKey,
  });
  const capacitySort = useTableSort({
    rows: capacityRows,
    columns: SERVICE_ANALYTICS_COLUMNS.capacity.columns,
    fallbackKey: SERVICE_ANALYTICS_COLUMNS.capacity.fallbackKey,
  });

  const renderInfoTooltip = (tooltipKey, label) => (
    <InfoTooltip
      text={SERVICE_ANALYTICS_TOOLTIPS[tooltipKey]}
      label={label}
      maxWidth={TOOLTIP_MAX_WIDTH}
    />
  );

  const renderSortIndicator = (sortState, key) => {
    if (sortState.key !== key) return null;
    return (
      <span className="text-[10px] leading-none text-slate-500">
        {SORT_ICON[sortState.dir] || SORT_ICON.desc}
      </span>
    );
  };

  const renderSortableHeader = ({
    label,
    sortKey,
    sortState,
    onSort,
    tooltipKey,
    align = "left",
  }) => {
    const isCenter = align === "center" || align === "right";
    const alignClass = isCenter ? "report-th-inner-center" : "report-th-inner-left";
    const buttonClass = isCenter
      ? "report-th-button-center font-semibold text-slate-600 hover:text-slate-800"
      : "report-th-button-left font-semibold text-slate-600 hover:text-slate-800";
    return (
      <div className={alignClass}>
        <button type="button" onClick={() => onSort(sortKey)} className={buttonClass}>
          <span>{label}</span>
          {renderSortIndicator(sortState, sortKey)}
        </button>
        {tooltipKey ? renderInfoTooltip(tooltipKey, label) : null}
      </div>
    );
  };

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Service Analytics
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id="service-analytics-overview"
              className={getSectionClassName("service-analytics-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_service_analytics"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
            {error}
          </div>
        )}

        <div className="sticky top-0 z-10 bg-white pb-4">
          <div
            id="service-analytics-filters"
            className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
              "service-analytics-filters"
            )}`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div>
                <div className="inline-flex items-center gap-2">
                  <label className={LABEL_TEXT}>Start Date</label>
                  {renderInfoTooltip("start_date", "Start Date")}
                </div>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                />
              </div>
              <div>
                <div className="inline-flex items-center gap-2">
                  <label className={LABEL_TEXT}>End Date</label>
                  {renderInfoTooltip("end_date", "End Date")}
                </div>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                />
              </div>
              <div>
                <div className="inline-flex items-center gap-2">
                  <label className={LABEL_TEXT}>Outlier Cap (hours)</label>
                  {renderInfoTooltip("outlier_cap_hours", "Outlier Cap (Hours)")}
                </div>
                <select
                  value={outlierCap}
                  onChange={(e) => setOutlierCap(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                >
                  {CAP_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="inline-flex items-center gap-2">
                  <label className={LABEL_TEXT}>Employee</label>
                  {renderInfoTooltip("employee", "Employee")}
                </div>
                <select
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                >
                  <option value="">All employees</option>
                  {staffOptions.map((staff) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.display_name || staff.full_name || `Staff ${staff.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="inline-flex items-center gap-2">
                  <label className={LABEL_TEXT}>Service Category</label>
                  {renderInfoTooltip("service_category", "Service Category")}
                </div>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                  disabled={categoriesLoading}
                >
                  <option value="">All categories</option>
                  {categoriesLoading ? (
                    <option value="" disabled>
                      Loading...
                    </option>
                  ) : serviceCategoryOptions.length ? (
                    serviceCategoryOptions.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No categories found
                    </option>
                  )}
                </select>
              </div>
              <div>
                <div className="inline-flex items-center gap-2">
                  <label className={LABEL_TEXT}>Group By</label>
                  {renderInfoTooltip("group_by", "Group By")}
                </div>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                >
                  {GROUP_BY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Duration Mode
                </span>
                {renderInfoTooltip("duration_mode", "Duration Mode")}
                <button
                  type="button"
                  className={togglePillClass(durationMode === "SINGLE_SERVICE_ONLY")}
                  onClick={() =>
                    setDurationMode((prev) =>
                      prev === "ALLOCATED" ? "SINGLE_SERVICE_ONLY" : "ALLOCATED"
                    )
                  }
                >
                  Single-service tickets only
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Refunds
                </span>
                {renderInfoTooltip("include_refunds", "Include refunds")}
                <button
                  type="button"
                  className={togglePillClass(includeRefunds)}
                  onClick={() => setIncludeRefunds((prev) => !prev)}
                >
                  Include refunds
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Discounts
                </span>
                {renderInfoTooltip("include_discounts", "Include discounts")}
                <button
                  type="button"
                  className={togglePillClass(includeDiscounts)}
                  onClick={() => setIncludeDiscounts((prev) => !prev)}
                >
                  Include discounts
                </button>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <div className="inline-flex items-center gap-2">
                  <button type="button" onClick={resetFilters} className={BTN_OUTLINE}>
                    Clear
                  </button>
                  {renderInfoTooltip("clear", "Clear")}
                </div>
                <div className="inline-flex items-center gap-2">
                  <button
                    type="button"
                    onClick={fetchAll}
                    className={BTN_PRIMARY}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Run Report"}
                  </button>
                  {renderInfoTooltip("run_report", "Run Report")}
                </div>
              </div>
            </div>
          </div>

        <div className="mt-4 flex flex-wrap gap-2">
            {TABS.map((tab) => {
              const tooltipKey = TAB_TOOLTIP_KEYS[tab.key];
              return (
                <div key={tab.key} className="inline-flex items-center gap-2">
                  <button
                    type="button"
                    className={`${TAB_BASE} ${
                      activeTab === tab.key ? TAB_ACTIVE : TAB_INACTIVE
                    }`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                  {renderInfoTooltip(tooltipKey, tab.label)}
                </div>
              );
            })}
          </div>
        </div>

        <div
          id="service-analytics-details"
          className={`space-y-6 ${getSectionClassName("service-analytics-details")}`}
        >
          <div
            id="service-analytics-export"
            className={`rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 ${getSectionClassName(
              "service-analytics-export"
            )}`}
          >
            Export is not available on this report.
          </div>
          {activeTab === "overview" && (
            <div className="space-y-6">
              <div
                id="service-analytics-kpis"
                className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${getSectionClassName(
                  "service-analytics-kpis"
                )}`}
              >
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase text-slate-500">Gross Sales</div>
                  <div className="text-2xl font-semibold text-slate-900">
                    {formatMoney(tiles.gross_service_sales)}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase text-slate-500">Net Sales</div>
                  <div className="text-2xl font-semibold text-slate-900">
                    {formatMoney(tiles.net_service_sales)}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase text-slate-500">Services Sold</div>
                  <div className="text-2xl font-semibold text-slate-900">
                    {formatNumber(tiles.services_sold_qty, 0)}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase text-slate-500">Avg Net Price</div>
                  <div className="text-2xl font-semibold text-slate-900">
                    {tiles.avg_service_price_net != null
                      ? formatMoney(tiles.avg_service_price_net)
                      : "--"}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase text-slate-500">Avg Services/Ticket</div>
                  <div className="text-2xl font-semibold text-slate-900">
                    {tiles.avg_services_per_ticket != null
                      ? formatNumber(tiles.avg_services_per_ticket, 2)
                      : "--"}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase text-slate-500">Top 5 Share</div>
                  <div className="text-2xl font-semibold text-slate-900">
                    {formatPercent(tiles.top5_share_percent)}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase text-slate-500">Refund Rate</div>
                  <div className="text-2xl font-semibold text-slate-900">
                    {formatPercent(tiles.refund_rate_percent)}
                  </div>
                </div>
              </div>

              {series.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900">
                      Net Sales Trend
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm report-table">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-4 py-3 report-th-left font-semibold">
                            {renderSortableHeader({
                              label: "Period",
                              sortKey: "period",
                              sortState: overviewSort.sortState,
                              onSort: overviewSort.handleSort,
                            })}
                          </th>
                          <th className="px-4 py-3 report-th-center font-semibold">
                            {renderSortableHeader({
                              label: "Net Sales",
                              sortKey: "net_sales",
                              sortState: overviewSort.sortState,
                              onSort: overviewSort.handleSort,
                              align: "center",
                            })}
                          </th>
                          <th className="px-4 py-3 report-th-center font-semibold">
                            {renderSortableHeader({
                              label: "Qty",
                              sortKey: "qty",
                              sortState: overviewSort.sortState,
                              onSort: overviewSort.handleSort,
                              align: "center",
                              tooltipKey: "column_qty",
                            })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {overviewSort.sortedRows.map((row) => (
                          <tr key={row.period} className="border-t border-slate-100">
                            <td className="px-4 py-3 report-td-left text-slate-700">{row.period}</td>
                            <td className="px-4 py-3 report-td-center text-slate-700">
                              {formatMoney(row.net_sales)}
                            </td>
                            <td className="px-4 py-3 report-td-center text-slate-700">
                              {formatNumber(row.qty, 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === "servicePerformance" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">
                  Service Performance
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm report-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 report-th-left font-semibold">
                        {renderSortableHeader({
                          label: "Service",
                          sortKey: "service",
                          sortState: serviceTotalsSort.sortState,
                          onSort: serviceTotalsSort.handleSort,
                          tooltipKey: "column_service",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Qty",
                          sortKey: "qty",
                          sortState: serviceTotalsSort.sortState,
                          onSort: serviceTotalsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_qty",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Gross",
                          sortKey: "gross",
                          sortState: serviceTotalsSort.sortState,
                          onSort: serviceTotalsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_gross",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Discount",
                          sortKey: "discount",
                          sortState: serviceTotalsSort.sortState,
                          onSort: serviceTotalsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_discount",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Net",
                          sortKey: "net",
                          sortState: serviceTotalsSort.sortState,
                          onSort: serviceTotalsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_net",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "% of Net",
                          sortKey: "percent_net",
                          sortState: serviceTotalsSort.sortState,
                          onSort: serviceTotalsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_percent_net",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "% of Qty",
                          sortKey: "percent_qty",
                          sortState: serviceTotalsSort.sortState,
                          onSort: serviceTotalsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_percent_qty",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Avg Duration",
                          sortKey: "avg_duration",
                          sortState: serviceTotalsSort.sortState,
                          onSort: serviceTotalsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_avg_duration",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Total Duration",
                          sortKey: "total_duration",
                          sortState: serviceTotalsSort.sortState,
                          onSort: serviceTotalsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_total_duration",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceTotalsSort.sortedRows.map((row) => (
                      <tr
                        key={`service-${row.service_id || row.service_name}`}
                        className="border-t border-slate-100"
                      >
                        <td className="px-4 py-3 report-td-left text-slate-700">
                          {row.service_name || "Service"}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.qty, 0)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.gross_sales)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.discount_amount)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.net_sales)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatPercent(row.percent_of_total_net)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatPercent(row.percent_of_total_qty)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMinutes(row.avg_duration_minutes)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatHours(row.total_duration_minutes)}
                        </td>
                      </tr>
                    ))}
                    {!serviceTotals.length && (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          {loading ? "Loading..." : "No service totals found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "mixByEmployee" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">
                  Service Mix by Employee
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm report-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 report-th-left font-semibold">
                        {renderSortableHeader({
                          label: "Employee",
                          sortKey: "employee",
                          sortState: mixByEmployeeSort.sortState,
                          onSort: mixByEmployeeSort.handleSort,
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-left font-semibold">
                        {renderSortableHeader({
                          label: "Service",
                          sortKey: "service",
                          sortState: mixByEmployeeSort.sortState,
                          onSort: mixByEmployeeSort.handleSort,
                          tooltipKey: "column_service",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Qty",
                          sortKey: "qty",
                          sortState: mixByEmployeeSort.sortState,
                          onSort: mixByEmployeeSort.handleSort,
                          align: "center",
                          tooltipKey: "column_qty",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Net Sales",
                          sortKey: "net",
                          sortState: mixByEmployeeSort.sortState,
                          onSort: mixByEmployeeSort.handleSort,
                          align: "center",
                          tooltipKey: "column_net",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "% of Employee",
                          sortKey: "percent_employee",
                          sortState: mixByEmployeeSort.sortState,
                          onSort: mixByEmployeeSort.handleSort,
                          align: "center",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Avg Duration (min)",
                          sortKey: "avg_duration",
                          sortState: mixByEmployeeSort.sortState,
                          onSort: mixByEmployeeSort.handleSort,
                          align: "center",
                          tooltipKey: "column_avg_duration",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Total Duration (hrs)",
                          sortKey: "total_duration",
                          sortState: mixByEmployeeSort.sortState,
                          onSort: mixByEmployeeSort.handleSort,
                          align: "center",
                          tooltipKey: "column_total_duration",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {mixByEmployeeSort.sortedRows.map((row, index) => (
                      <tr
                        key={`${row.staff_id || "staff"}-${row.service_id || index}`}
                        className="border-t border-slate-100"
                      >
                        <td className="px-4 py-3 report-td-left text-slate-700">
                          {row.staff_name || "Unassigned"}
                        </td>
                        <td className="px-4 py-3 report-td-left text-slate-700">
                          {row.service_name || "Service"}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.qty, 0)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.net_sales)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatPercent(row.percent_of_employee_net)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMinutes(row.avg_duration_minutes)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatHours(row.total_duration_minutes)}
                        </td>
                      </tr>
                    ))}
                    {!serviceMix.length && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          {loading ? "Loading..." : "No service mix data found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "topServices" && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <label className={LABEL_TEXT}>Top N</label>
                  <select
                    value={topN}
                    onChange={(e) => setTopN(Number(e.target.value))}
                    className={FIELD_CLASS}
                  >
                    {TOP_N_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Sort By</label>
                  <select
                    value={topSort}
                    onChange={(e) => setTopSort(e.target.value)}
                    className={FIELD_CLASS}
                  >
                    {TOP_SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button type="button" onClick={fetchAll} className={BTN_PRIMARY}>
                  Refresh Top Services
                </button>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900">Top Services</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm report-table">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-4 py-3 report-th-left font-semibold">
                          {renderSortableHeader({
                            label: "Service",
                            sortKey: "service",
                            sortState: topServicesSort.sortState,
                            onSort: topServicesSort.handleSort,
                            tooltipKey: "column_service",
                          })}
                        </th>
                        <th className="px-4 py-3 report-th-center font-semibold">
                          {renderSortableHeader({
                            label: "Qty",
                            sortKey: "qty",
                            sortState: topServicesSort.sortState,
                            onSort: topServicesSort.handleSort,
                            align: "center",
                            tooltipKey: "column_qty",
                          })}
                        </th>
                        <th className="px-4 py-3 report-th-center font-semibold">
                          {renderSortableHeader({
                            label: "Net Sales",
                            sortKey: "net_sales",
                            sortState: topServicesSort.sortState,
                            onSort: topServicesSort.handleSort,
                            align: "center",
                            tooltipKey: "column_net",
                          })}
                        </th>
                        <th className="px-4 py-3 report-th-center font-semibold">
                          {renderSortableHeader({
                            label: "% of Total",
                            sortKey: "percent_total",
                            sortState: topServicesSort.sortState,
                            onSort: topServicesSort.handleSort,
                            align: "center",
                            tooltipKey: "column_percent_net",
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topServicesSort.sortedRows.map((row, index) => (
                        <tr
                          key={`top-service-${row.service_id || index}`}
                          className="border-t border-slate-100"
                        >
                          <td className="px-4 py-3 report-td-left text-slate-700">
                            {row.service_name || "Service"}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.qty, 0)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatMoney(row.net_sales)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatPercent(row.percent_of_total)}
                          </td>
                        </tr>
                      ))}
                      {!topServices.length && (
                        <tr>
                          <td
                            colSpan={4}
                            className="px-4 py-6 text-center text-slate-500"
                          >
                            {loading ? "Loading..." : "No top services found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === "clientsByService" && (
            <div className="space-y-4">
              <div>
                <label className={LABEL_TEXT}>Service</label>
                <select
                  value={clientsServiceId}
                  onChange={(e) => setClientsServiceId(e.target.value)}
                  className={`${FIELD_CLASS} w-full`}
                >
                  <option value="">Select a service</option>
                  {serviceOptions.map((svc) => (
                    <option key={svc.id} value={svc.id}>
                      {svc.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Clients by Service
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm report-table">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-4 py-3 report-th-left font-semibold">
                          {renderSortableHeader({
                            label: "Client",
                            sortKey: "client",
                            sortState: clientsByServiceSort.sortState,
                            onSort: clientsByServiceSort.handleSort,
                          })}
                        </th>
                        <th className="px-4 py-3 report-th-center font-semibold">
                          {renderSortableHeader({
                            label: "Times",
                            sortKey: "times",
                            sortState: clientsByServiceSort.sortState,
                            onSort: clientsByServiceSort.handleSort,
                            align: "center",
                            tooltipKey: "column_qty",
                          })}
                        </th>
                        <th className="px-4 py-3 report-th-center font-semibold">
                          {renderSortableHeader({
                            label: "Net Spend",
                            sortKey: "net_spend",
                            sortState: clientsByServiceSort.sortState,
                            onSort: clientsByServiceSort.handleSort,
                            align: "center",
                            tooltipKey: "column_net",
                          })}
                        </th>
                        <th className="px-4 py-3 report-th-center font-semibold">
                          {renderSortableHeader({
                            label: "Last Purchase",
                            sortKey: "last_purchase",
                            sortState: clientsByServiceSort.sortState,
                            onSort: clientsByServiceSort.handleSort,
                            align: "center",
                          })}
                        </th>
                        <th className="px-4 py-3 report-th-center font-semibold">
                          {renderSortableHeader({
                            label: "Last Visit",
                            sortKey: "last_visit",
                            sortState: clientsByServiceSort.sortState,
                            onSort: clientsByServiceSort.handleSort,
                            align: "center",
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientsByServiceSort.sortedRows.map((row) => (
                        <tr
                          key={`client-${row.client_id}`}
                          className="border-t border-slate-100"
                        >
                          <td className="px-4 py-3 report-td-left text-slate-700">
                            {row.client_name || "Client"}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatNumber(row.times_purchased_in_range, 0)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {formatMoney(row.total_spend_on_service_net)}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {row.last_time_purchased_this_service || "--"}
                          </td>
                          <td className="px-4 py-3 report-td-center text-slate-700">
                            {row.last_visit_date_overall || "--"}
                          </td>
                        </tr>
                      ))}
                      {!clientsByService.length && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-6 text-center text-slate-500"
                          >
                            {clientsServiceId
                              ? "No clients found."
                              : "Select a service to view clients."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === "retention" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">
                  Service Retention
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm report-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 report-th-left font-semibold">
                        {renderSortableHeader({
                          label: "Service",
                          sortKey: "service",
                          sortState: retentionSort.sortState,
                          onSort: retentionSort.handleSort,
                          tooltipKey: "column_service",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Unique Clients",
                          sortKey: "unique_clients",
                          sortState: retentionSort.sortState,
                          onSort: retentionSort.handleSort,
                          align: "center",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "New Clients",
                          sortKey: "new_clients",
                          sortState: retentionSort.sortState,
                          onSort: retentionSort.handleSort,
                          align: "center",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Repeat Clients",
                          sortKey: "repeat_clients",
                          sortState: retentionSort.sortState,
                          onSort: retentionSort.handleSort,
                          align: "center",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Repeat 30d",
                          sortKey: "repeat_30",
                          sortState: retentionSort.sortState,
                          onSort: retentionSort.handleSort,
                          align: "center",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Repeat 60d",
                          sortKey: "repeat_60",
                          sortState: retentionSort.sortState,
                          onSort: retentionSort.handleSort,
                          align: "center",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Repeat 90d",
                          sortKey: "repeat_90",
                          sortState: retentionSort.sortState,
                          onSort: retentionSort.handleSort,
                          align: "center",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {retentionSort.sortedRows.map((row) => (
                      <tr key={`ret-${row.service_id}`} className="border-t border-slate-100">
                        <td className="px-4 py-3 report-td-left text-slate-700">
                          {row.service_name || "Service"}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.unique_clients_in_range, 0)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.new_clients_for_service, 0)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.repeat_clients, 0)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatPercent(row.repeat_rate_30)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatPercent(row.repeat_rate_60)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatPercent(row.repeat_rate_90)}
                        </td>
                      </tr>
                    ))}
                    {!retentionRows.length && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          {loading ? "Loading..." : "No retention data found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "combos" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">
                  Add-ons / Combos
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm report-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 report-th-left font-semibold">
                        {renderSortableHeader({
                          label: "Service A",
                          sortKey: "service_a",
                          sortState: combosSort.sortState,
                          onSort: combosSort.handleSort,
                          tooltipKey: "column_service",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-left font-semibold">
                        {renderSortableHeader({
                          label: "Service B",
                          sortKey: "service_b",
                          sortState: combosSort.sortState,
                          onSort: combosSort.handleSort,
                          tooltipKey: "column_service",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Bundle Count",
                          sortKey: "bundle_count",
                          sortState: combosSort.sortState,
                          onSort: combosSort.handleSort,
                          align: "center",
                          tooltipKey: "column_qty",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Bundle Net",
                          sortKey: "bundle_net",
                          sortState: combosSort.sortState,
                          onSort: combosSort.handleSort,
                          align: "center",
                          tooltipKey: "column_net",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Attach Rate",
                          sortKey: "attach_rate",
                          sortState: combosSort.sortState,
                          onSort: combosSort.handleSort,
                          align: "center",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {combosSort.sortedRows.map((row, index) => (
                      <tr
                        key={`combo-${row.service_a_id || index}-${row.service_b_id || index}`}
                        className="border-t border-slate-100"
                      >
                        <td className="px-4 py-3 report-td-left text-slate-700">
                          {row.service_a_name || "Service"}
                        </td>
                        <td className="px-4 py-3 report-td-left text-slate-700">
                          {row.service_b_name || "Service"}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.bundle_count, 0)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.bundle_net_sales)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatPercent((row.attach_rate ?? 0) * 100)}
                        </td>
                      </tr>
                    ))}
                    {!combosRows.length && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          {loading ? "Loading..." : "No combo data found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "discounts" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Discounts</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm report-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 report-th-left font-semibold">
                        {renderSortableHeader({
                          label: "Service",
                          sortKey: "service",
                          sortState: discountsSort.sortState,
                          onSort: discountsSort.handleSort,
                          tooltipKey: "column_service",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Qty",
                          sortKey: "qty",
                          sortState: discountsSort.sortState,
                          onSort: discountsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_qty",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Gross",
                          sortKey: "gross",
                          sortState: discountsSort.sortState,
                          onSort: discountsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_gross",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Discount",
                          sortKey: "discount",
                          sortState: discountsSort.sortState,
                          onSort: discountsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_discount",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "% Discount",
                          sortKey: "discount_percent",
                          sortState: discountsSort.sortState,
                          onSort: discountsSort.handleSort,
                          align: "center",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Net",
                          sortKey: "net",
                          sortState: discountsSort.sortState,
                          onSort: discountsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_net",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Avg Net",
                          sortKey: "avg_net",
                          sortState: discountsSort.sortState,
                          onSort: discountsSort.handleSort,
                          align: "center",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Base Price",
                          sortKey: "base_price",
                          sortState: discountsSort.sortState,
                          onSort: discountsSort.handleSort,
                          align: "center",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {discountsSort.sortedRows.map((row, index) => (
                      <tr
                        key={`discount-${row.service_id || index}`}
                        className="border-t border-slate-100"
                      >
                        <td className="px-4 py-3 report-td-left text-slate-700">
                          {row.service_name || "Service"}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.qty, 0)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.gross_sales)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.discount_amount)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatPercent(row.discount_percent)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.net_sales)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {row.avg_net_price != null
                            ? formatMoney(row.avg_net_price)
                            : "--"}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.base_price)}
                        </td>
                      </tr>
                    ))}
                    {!discountRows.length && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          {loading ? "Loading..." : "No discount data found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "refunds" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Refunds</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm report-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 report-th-left font-semibold">
                        {renderSortableHeader({
                          label: "Service",
                          sortKey: "service",
                          sortState: refundsSort.sortState,
                          onSort: refundsSort.handleSort,
                          tooltipKey: "column_service",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Refund Qty",
                          sortKey: "refund_qty",
                          sortState: refundsSort.sortState,
                          onSort: refundsSort.handleSort,
                          align: "center",
                          tooltipKey: "column_qty",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Refund Amount",
                          sortKey: "refund_amount",
                          sortState: refundsSort.sortState,
                          onSort: refundsSort.handleSort,
                          align: "center",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Refund Rate",
                          sortKey: "refund_rate",
                          sortState: refundsSort.sortState,
                          onSort: refundsSort.handleSort,
                          align: "center",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {refundsSort.sortedRows.map((row, index) => (
                      <tr
                        key={`refund-${row.service_id || index}`}
                        className="border-t border-slate-100"
                      >
                        <td className="px-4 py-3 report-td-left text-slate-700">
                          {row.service_name || "Service"}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.refund_qty, 0)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.refund_amount)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatPercent(row.refund_rate_percent)}
                        </td>
                      </tr>
                    ))}
                    {!refundRows.length && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          {loading ? "Loading..." : "No refund data found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "capacity" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Capacity</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm report-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 report-th-left font-semibold">
                        {renderSortableHeader({
                          label: "Service",
                          sortKey: "service",
                          sortState: capacitySort.sortState,
                          onSort: capacitySort.handleSort,
                          tooltipKey: "column_service",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Qty",
                          sortKey: "qty",
                          sortState: capacitySort.sortState,
                          onSort: capacitySort.handleSort,
                          align: "center",
                          tooltipKey: "column_qty",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Net Sales",
                          sortKey: "net_sales",
                          sortState: capacitySort.sortState,
                          onSort: capacitySort.handleSort,
                          align: "center",
                          tooltipKey: "column_net",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Total Duration (hrs)",
                          sortKey: "total_hours",
                          sortState: capacitySort.sortState,
                          onSort: capacitySort.handleSort,
                          align: "center",
                          tooltipKey: "column_total_duration",
                        })}
                      </th>
                      <th className="px-4 py-3 report-th-center font-semibold">
                        {renderSortableHeader({
                          label: "Net / Hour",
                          sortKey: "net_per_hour",
                          sortState: capacitySort.sortState,
                          onSort: capacitySort.handleSort,
                          align: "center",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {capacitySort.sortedRows.map((row, index) => (
                      <tr
                        key={`capacity-${row.service_id || index}`}
                        className="border-t border-slate-100"
                      >
                        <td className="px-4 py-3 report-td-left text-slate-700">
                          {row.service_name || "Service"}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatNumber(row.qty, 0)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {formatMoney(row.net_sales)}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {row.total_hours_sold != null
                            ? Number(row.total_hours_sold).toFixed(1)
                            : "--"}
                        </td>
                        <td className="px-4 py-3 report-td-center text-slate-700">
                          {row.net_per_hour != null
                            ? formatMoney(row.net_per_hour)
                            : "--"}
                        </td>
                      </tr>
                    ))}
                    {!capacityRows.length && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          {loading ? "Loading..." : "No capacity data found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  </div>
  );
}

